import React, { useEffect, useRef, useState } from "react";
import { useIsFetching } from "react-query";

/**
 * Thin NProgress-style loading bar at the top of the page.
 * Appears whenever react-query has active background/foreground fetches.
 * Fades out smoothly once all fetches complete.
 */
export const TopProgressBar: React.FC = () => {
  const isFetching = useIsFetching();
  const [visible, setVisible] = useState(false);
  const [width, setWidth] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isFetching > 0) {
      // Start the bar
      setVisible(true);
      setWidth(0);
      // Quickly jump to 30%, then crawl toward 85%
      requestAnimationFrame(() => {
        setWidth(30);
        animRef.current = setTimeout(() => setWidth(65), 300);
        timerRef.current = setTimeout(() => setWidth(85), 900);
      });
    } else {
      // Finish the bar
      if (animRef.current) clearTimeout(animRef.current);
      if (timerRef.current) clearTimeout(timerRef.current);
      setWidth(100);
      // Hide after the fill animation completes
      timerRef.current = setTimeout(() => {
        setVisible(false);
        setWidth(0);
      }, 350);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (animRef.current) clearTimeout(animRef.current);
    };
  }, [isFetching]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9999,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${width}%`,
          background: "linear-gradient(90deg, #00A19C 0%, #26C6DA 100%)",
          transition: width === 100
            ? "width 0.25s ease-out"
            : "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "0 0 8px rgba(0, 161, 156, 0.6)",
          borderRadius: "0 2px 2px 0",
        }}
      />
    </div>
  );
};
